import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import axios from '../../../api/axios';
import { useDebounce } from '../../../hooks/useDebounce';
import { Candidate, PaginationData, SortConfig } from '../../../types';
import { getSortIcon, renderPagination } from '../../../utils/Utils';
import AdminHeader from '../layout/AdminHeader';
import AdminNavigation from '../layout/AdminNavigation';
import './AdminCandidateList.css';

const AdminCandidateList: React.FC = () => {
  const [candidates, setCandidates] = useState<PaginationData>({} as PaginationData);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDeleted, setShowDeleted] = useState<boolean>(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchCandidates();
  }, [currentPage, sortConfig, debouncedSearchTerm, showDeleted]);


  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        sort: sortConfig.key,
        direction: sortConfig.direction,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(showDeleted && { show_deleted: 'true' })
      });

      const response = await axios.get(`/admin/candidates?${params}`);
      setCandidates(response.data);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      setError('候補者一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string): void => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleShowDeletedChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setShowDeleted(e.target.checked);
    setCurrentPage(1);
  };

  const handleDelete = async (candidateId: number, candidateName: string) => {
    if (!window.confirm(`${candidateName}さんの情報を削除してもよろしいですか？`)) {
      return;
    }

    try {
      await axios.delete(`/admin/candidates/${candidateId}`);
      alert(`${candidateName}さんの情報が削除されました。`);
      fetchCandidates();
    } catch (error: any) {
      alert(error.response?.data?.message || '削除に失敗しました。');
    }
  };

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  return (
    <div className="dashboard-container">
      <AdminHeader />

      <Container>

        <div className="navigation-container">
          <AdminNavigation candidates={!showDeleted ? candidates.total : undefined} />
          <div className="search-container">
            <div className="search-input-container">
              <Form>
              <Form.Control
                type="text"
                placeholder="候補者名／メールアドレス／電話番号／クライアント名"
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
              </Form>
            </div>
          </div>
        </div>

        <div className="search-deleted-container">
          <Form.Check
            type="checkbox"
            id="show-deleted-candidates"
            label="削除されたデータのみ表示"
            checked={showDeleted}
            onChange={handleShowDeletedChange}
          />
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="candidates-table-container">
          <table className="candidates-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className="sortable">
                  候補者名 {getSortIcon('name', sortConfig)}
                </th>
                <th onClick={() => handleSort('user.name')} className="sortable">
                  クライアント名 {getSortIcon('user.name', sortConfig)}
                </th>
                <th onClick={() => handleSort('phone')} className="sortable">
                  電話番号 {getSortIcon('phone', sortConfig)}
                </th>
                <th onClick={() => handleSort('email')} className="sortable">
                  メールアドレス {getSortIcon('email', sortConfig)}
                </th>
                <th className="text-center">回答数</th>
                <th onClick={() => handleSort('created_at')} className="sortable">
                  登録日 {getSortIcon('created_at', sortConfig)}
                </th>
                <th className="text-center">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center">
                    <div className="blank-form-container">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3">情報を読み込んでいます...</p>
                    </div>
                  </td>
                </tr>
              ) : (
              candidates.data && candidates.data.length > 0 ? (
                candidates.data?.map((candidate: Candidate) => (
                  <tr key={candidate.id}>
                    <td>{candidate.name}</td>
                    <td>{candidate.user?.name || '-'}</td>
                    <td>{candidate.phone || '-'}</td>
                    <td>{candidate.email}</td>
                    <td className="text-center">{candidate.respondents_count || 0} / {candidate.responses_count || 0}</td>
                    <td>{candidate.created_at ? new Date(candidate.created_at).toLocaleDateString('ja-JP') : '-'}</td>
                    <td className="text-center">
                    {((candidate.deleted_at || '') === '') && (
                      <>
                        <Button 
                          className="detail-button me-1"
                          onClick={() => navigate(`/admin/candidates/${candidate.id}`)}
                        >
                          詳細
                        </Button>
                        <Button 
                          className="delete-button-icon"
                          onClick={() => handleDelete(candidate.id, candidate.name)}
                          title="削除"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                      </>
                    )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center">該当するデータはありません。</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {candidates.last_page > 1 && renderPagination(currentPage, candidates.last_page, paginate)}
      </Container>
    </div>
  );
};

export default AdminCandidateList;
